/* istanbul ignore file */
import { useState, useEffect } from 'react'
import useScript from 'react-script-hook'
import gql from 'graphql-tag'
import { error as logError } from '@divvy-web/logger'

const MX_CONNECT_JS_URL = 'https://atrium.mx.com/connect.js'

const GET_MX_WIDGET_URL = gql`
  query GetAtriumWidgetUrl {
    atriumWidgetUrl
  }
`

export const useMXWidget = ({ anchorId, client, config = {}, onSuccess }) => {
  const [loading, error] = useScript({ src: MX_CONNECT_JS_URL })
  const [ready, setReady] = useState(false)
  const [atriumUrl, setAtriumUrl] = useState(null)
  const [retries, setRetries] = useState(0)
  const [urlError, setUrlError] = useState()

  useEffect(() => {
    if (loading) return
    if (error) return

    if (!window.MXConnect) {
      const eventName = 'accounting-integrations-mx-connect-is-not-set'
      const message = 'window.MXConnect is not set'

      try {
        logError({
          channel: 'account-integrations-mx-error',
          error: message,
          eventName,
          squad: 'accounting-integrations',
          squadCode: 'ACC'
        })
      } catch (err) {
        console.error('[WzAwNF0=] Unable to initialize New Relic')
      }

      // I don't know why but even after `loading` is false, sometimes in production `window.Plaid` is not set
      // yet, so we retry every 50ms for 5 seconds
      if (retries < 100) {
        // eslint-disable-next-line no-console
        console.error('retrying in a 50ms to see if window.Plaid loads')
        setTimeout(() => {
          setRetries(retries + 1)
        }, 50)
      }
      return
    }

    setReady(true)

    if (atriumUrl) {
      const mxConnect = new window.MXConnect({
        config,
        id: anchorId,
        onSuccess,
        url: decodeURIComponent(atriumUrl)
      })
      mxConnect.load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, retries, atriumUrl, anchorId, onSuccess])

  const open = () => {
    client.query({
      fetchPolicy: 'no-cache',
      query: GET_MX_WIDGET_URL
    }).then(({ data }) => {
      setAtriumUrl(data?.atriumWidgetUrl)
    }).catch(error => setUrlError(error))
  }

  return {
    error: error || urlError,
    open: ready ? open : () => {},
    ready
  }
}
